import { ArrowForward, Create, Delete } from '@mui/icons-material'
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Fade,
	IconButton,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Theme,
	Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { percent } from 'csx'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useMemo } from 'react'
import { useHistory } from 'react-router'
import {
	BaseWorkflowFailureActionInstance,
	Workflow,
	WorkflowFailureActionConfiguration,
	WorkflowFailureActionInstanceMetadata,
} from '../../../../../api/clients/workflows/DTOs'
import WorkflowFailureActionInstancesClient from '../../../../../api/clients/workflows/WorkflowFailureActionInstancesClient'
import { useModals } from '../../../../../services/notifications/ModalService'
import { usePrompts } from '../../../../../services/notifications/PromptService'
import { toastService } from '../../../../../services/notifications/ToastService'
import { FixedHeaderWithHint } from '../../../../../utils/HOC/FixedHeaders'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import { WorkflowTabs } from '../WorkflowTabs'
import { ConfirmDeleteWorkflowFailureActionDialog } from './DeleteWorkflowFailureActionDialog'

type WorkflowFailureActionsDashboardProps = {
	workflow: Workflow
	instances: WorkflowFailureActionInstanceMetadata[]
	failureActions: WorkflowFailureActionConfiguration[]
	onUpdateFailureActions: (
		newFailureActionsList: WorkflowFailureActionInstanceMetadata[],
	) => void
	onTabIndexChanged: (newValue: string) => void
}

export const WorkflowFailureActionsDashboard = (
	props: WorkflowFailureActionsDashboardProps,
) => {
	const modalService = useModals()
	const promptService = usePrompts()

	const history = useHistory()
	const styles = useStyles()

	const failureActionInstancesClient =
		new WorkflowFailureActionInstancesClient()

	const handleCreateFailureActionClicked = () => {
		modalService
			.showForm((modalProps) => (
				<CreateWorkflowFailureActionDialog
					workflowId={props.workflow.id}
					failureActionNames={props.instances.map((v) => v.displayName)}
					failureActions={props.failureActions}
					onClose={() => modalProps.close({ closeResult: 'cancel' })}
					onCreate={(v) => modalProps.close({ closeResult: 'okay', value: v })}
				/>
			))
			.then(async (result) => {
				if (result.closeResult === 'okay')
					try {
						const { data: createdInstance } =
							await failureActionInstancesClient.create(
								result.value as BaseWorkflowFailureActionInstance,
							)

						toastService.displayToast({
							message: 'Failure action created successfully',
							area: 'global',
							severity: 'success',
						})

						history.push(`_failure-actions/${createdInstance.id}`)
					} catch (e) {
						console.error(e)
						toastService.displayToast({
							message: 'Failed to create failure action',
							area: 'global',
							severity: 'error',
						})
					}
			})
	}

	const handleDeleteFailureAction = async (id: number) => {
		try {
			await failureActionInstancesClient.deleteInstance(id)

			props.onUpdateFailureActions(props.instances.filter((v) => v.id !== id))

			toastService.displayToast({
				message: 'Failure action deleted',
				area: 'global',
				severity: 'success',
			})
		} catch (e) {
			console.error(e)
			toastService.displayToast({
				message: 'Failed to delete failure action',
				area: 'global',
				severity: 'error',
			})
		}
	}

	return (
		<AdministrationPageContainer
			title={props.workflow.name}
			actions={
				<Box display="flex" justifyContent="center" alignItems="center">
					<Button
						startIcon={<Create />}
						variant="contained"
						onClick={handleCreateFailureActionClicked}
					>
						Create Failure Action
					</Button>
				</Box>
			}
		>
			<Box display="flex" flexDirection="column" width="100%">
				<WorkflowTabs
					currentTab="failure-actions"
					onTabChanged={(_evt, newValue) => props.onTabIndexChanged(newValue)}
				/>
				<Divider />
				<Fade in>
					<Box display="flex" flexDirection="column" height="100%" width="100%">
						<Box marginY={2}>
							<TableContainer className={styles.tableRoot} component={Paper}>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Name</TableCell>
											<TableCell>Type</TableCell>
											<TableCell>Description</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{props.instances.map((instance, i) => {
											return (
												<TableRow key={i}>
													<TableCell>{instance.displayName}</TableCell>
													<TableCell>{instance.failureActionType}</TableCell>
													<TableCell>{instance.description}</TableCell>
													<TableCell align="right">
														<IconButton
															onClick={() => {
																promptService
																	.showDialog((props) => (
																		<ConfirmDeleteWorkflowFailureActionDialog
																			name={instance.displayName}
																			onCancel={() => props.close('cancel')}
																			onConfirm={() => props.close('okay')}
																		/>
																	))
																	.then(async (v) => {
																		if (v == 'okay') {
																			await handleDeleteFailureAction(
																				instance.id,
																			)
																		}
																	})
															}}
														>
															<Delete fontSize="small" />
														</IconButton>

														<IconButton
															onClick={() =>
																history.push(`_failure-actions/${instance.id}`)
															}
														>
															<ArrowForward fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					</Box>
				</Fade>
			</Box>
		</AdministrationPageContainer>
	)
}

type CreateWorkflowFailureActionDialogProps = {
	workflowId: number
	failureActionNames: string[]
	failureActions: WorkflowFailureActionConfiguration[]
	onClose: () => void
	onCreate: (baseModel: BaseWorkflowFailureActionInstance) => void
}

const CreateWorkflowFailureActionDialog = observer(
	(props: CreateWorkflowFailureActionDialogProps) => {
		const localStore = useLocalObservable(() => ({
			name: '',
			description: '',
			failureAction: props.failureActions[0] ?? undefined,
			nameExists: false,
			isSubmitting: false,
		}))

		const upperInstanceNames = useMemo(
			() => props.failureActionNames.map((v) => v.toUpperCase().trim()),
			[props.failureActionNames],
		)

		const handleCreateFailureAction = () => {
			if (localStore.nameExists || localStore.failureAction === undefined)
				return

			props.onCreate({
				displayName: localStore.name,
				description: localStore.description,
				failureActionId: localStore.failureAction.failureActionId,
				state: {},
				workflowId: props.workflowId,
			})
		}

		return (
			<>
				<DialogTitle>Create Failure Action</DialogTitle>
				<DialogContent>
					<FixedHeaderWithHint
						label="Name"
						hint={
							localStore.nameExists &&
							'A failure action with this name already exists'
						}
						hintColor="error.main"
					>
						<TextField
							fullWidth
							error={localStore.nameExists}
							value={localStore.name}
							onChange={action((evt) => {
								localStore.name = evt.target.value

								if (
									upperInstanceNames.includes(
										localStore.name.toUpperCase().trim(),
									)
								)
									localStore.nameExists = true
								else localStore.nameExists = false
							})}
							disabled={localStore.isSubmitting}
							autoComplete="off"
						/>
					</FixedHeaderWithHint>
					<FixedHeaderWithHint label="Description">
						<TextField
							fullWidth
							value={localStore.description}
							onChange={action(
								(evt) => (localStore.description = evt.target.value),
							)}
						/>
					</FixedHeaderWithHint>
					<Select
						fullWidth
						size="small"
						defaultValue={localStore.failureAction?.failureActionId ?? ''}
						value={localStore.failureAction?.failureActionId ?? ''}
						onChange={action((evt: SelectChangeEvent<string>) => {
							localStore.failureAction =
								props.failureActions.find(
									(v) => v.failureActionId === evt.target.value,
								) ?? props.failureActions[0]
						})}
					>
						{props.failureActions.map((failureAction, i) => (
							<MenuItem key={i} value={failureAction.failureActionId ?? ''}>
								<div>
									<Typography fontWeight="bold">
										{failureAction.displayName}
									</Typography>
									<Typography fontStyle="italic" fontSize="small">
										{failureAction.description}
									</Typography>
								</div>
							</MenuItem>
						))}
					</Select>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={props.onClose}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleCreateFailureAction}
						disabled={
							localStore.nameExists ||
							localStore.name === '' ||
							localStore.failureAction === undefined
						}
					>
						Create
					</Button>
				</DialogActions>
			</>
		)
	},
)

const useStyles = makeStyles((theme: Theme) => ({
	tableRoot: {
		width: percent(100),
		marginBottom: theme.spacing(3),
		height: 'fit-content',
		overflow: 'auto',
	},
}))
